import {useState, useEffect} from 'react'
import {projectFirestore, timestamp} from '../firebase/config'
import {CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_CLOUD_NAME} from '../cloudinary/config'

// رفع صورة لحساب Cloudinary واحد
function uploadToCloudinary(file, account, folder) {
    return new Promise((resolve, reject) => {
        const cloudName = account?.cloudName || CLOUDINARY_CLOUD_NAME;
        const preset = account?.preset || CLOUDINARY_UPLOAD_PRESET;
        const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', preset);
        formData.append('folder', folder);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                const perc = (e.loaded / e.total) * 100;
                if (resolve._onProgress) resolve._onProgress(perc);
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const response = JSON.parse(xhr.responseText);
                resolve(response.secure_url);
            } else {
                reject(new Error('Upload failed: ' + xhr.status));
            }
        };

        xhr.onerror = () => reject(new Error('Connection error'));
        xhr.send(formData);
    });
}

const useStorage = (file, collection) => {
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [url, setUrl] = useState(null);
    const [backgroundStatus, setBackgroundStatus] = useState('');

    useEffect(() => {
        if (!file || !collection) return;

        const doUpload = async () => {
            try {
                // جلب إعدادات المصادر
                const settingsDoc = await projectFirestore.collection('settings').doc('imageSource').get();
                const settings = settingsDoc.exists ? settingsDoc.data() : {};
                
                const cloudAccounts = (settings.cloudinaryAccounts || []).filter(a => a.enabled !== false);
                const activeCloudId = settings.activeCloudinary || '';
                
                // تحديد الحساب الأساسي للرفع
                let primaryAccount = cloudAccounts.find(a => a.id === activeCloudId);
                if (!primaryAccount && cloudAccounts.length > 0) primaryAccount = cloudAccounts[0];
                if (!primaryAccount) {
                    // حساب افتراضي من الإعدادات المحلية
                    primaryAccount = { id: 'default', cloudName: CLOUDINARY_CLOUD_NAME, preset: CLOUDINARY_UPLOAD_PRESET };
                }

                // الرفع الأساسي مع تتبع التقدم
                let primaryUrl = null;
                let primaryAccountId = primaryAccount.id;
                
                const uploadWithProgress = uploadToCloudinary(file, primaryAccount, collection);
                uploadWithProgress._onProgress = setProgress;
                
                try {
                    primaryUrl = await uploadWithProgress;
                } catch (primaryError) {
                    // فشل الحساب الأساسي — جرب حسابات أخرى
                    console.warn('Primary upload failed, trying fallback...', primaryError);
                    const fallbacks = cloudAccounts.filter(a => a.id !== primaryAccount.id);
                    for (const fallback of fallbacks) {
                        try {
                            const fbUpload = uploadToCloudinary(file, fallback, collection);
                            fbUpload._onProgress = setProgress;
                            primaryUrl = await fbUpload;
                            primaryAccountId = fallback.id;
                            break;
                        } catch (e) {
                            console.warn('Fallback upload failed:', fallback.label, e);
                        }
                    }
                }

                if (!primaryUrl) {
                    setError('فشل رفع الصورة لجميع المصادر');
                    return;
                }

                // حفظ في Firestore
                const collectionRef = projectFirestore.collection(collection);
                const createdAt = timestamp();
                const docRef = await collectionRef.add({
                    url: primaryUrl,
                    sources: { [primaryAccountId]: primaryUrl },
                    createdAt,
                    order: Date.now(),
                });

                setUrl(primaryUrl);

                // رفع في الخلفية لباقي الحسابات المفعلة (بدون حظر UI)
                const otherAccounts = cloudAccounts.filter(a => a.id !== primaryAccountId);
                if (otherAccounts.length > 0) {
                    setBackgroundStatus('جاري النسخ الاحتياطي...');
                    
                    const bgPromises = otherAccounts.map(async (acc) => {
                        try {
                            const bgUrl = await uploadToCloudinary(file, acc, collection);
                            await docRef.update({ ['sources.' + acc.id]: bgUrl });
                            return { id: acc.id, success: true };
                        } catch (e) {
                            console.warn('Background upload failed:', acc.label, e);
                            return { id: acc.id, success: false };
                        }
                    });

                    Promise.all(bgPromises).then((results) => {
                        const successCount = results.filter(r => r.success).length;
                        setBackgroundStatus(successCount > 0 
                            ? `تم النسخ الاحتياطي (${successCount}/${otherAccounts.length})`
                            : '');
                        // مسح الحالة بعد 3 ثواني
                        setTimeout(() => setBackgroundStatus(''), 3000);
                    });
                }

            } catch (err) {
                console.error('Upload error:', err);
                setError('حدث خطأ أثناء رفع الصورة');
            }
        };

        doUpload();
    }, [file, collection]);

    return { progress, url, error, backgroundStatus };
};

export default useStorage
